import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Link2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { UniverseCompanyDialog } from "@/components/UniverseCompanyDialog";
import { Button } from "@/components/ui/button";

export function UniverseLinkBanner({ entityType, record, onLinked }) {
  const [addOpen, setAddOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const company = (record?.company || "").trim();

  const { data: matches = [] } = useQuery({
    queryKey: ["uni-banner-match", entityType, record?.id, company],
    queryFn: async () => (await api.get("/master-data/match", { params: { q: company } })).data,
    enabled: !!company,
  });

  const link = async (m) => {
    setLinking(true);
    try {
      const path = entityType === "lead" ? `/leads/${record.id}` : `/opportunities/${record.id}`;
      await api.put(path, { universe_id: m.id, universe_company: m.company });
      toast.success(`Linked to “${m.company}” in the Universe`);
      onLinked?.();
    } catch (e) { toast.error(apiError(e)); } finally { setLinking(false); }
  };

  const isLead = entityType === "lead";
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3" data-testid="universe-link-banner">
      <div className="flex gap-2.5 items-start">
        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">“{company || "This company"}” is not linked to the Universe</p>
          <p className="text-xs text-amber-800 mt-0.5">Every account must exist in the Universe master. Link an existing company below, or add it in one click.</p>
          {matches.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {matches.slice(0, 3).map((m) => (
                <button key={m.id} type="button" disabled={linking} onClick={() => link(m)}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-900 bg-white border border-amber-300 hover:bg-amber-100 px-2 py-1 rounded transition-colors disabled:opacity-50"
                  data-testid={`universe-banner-match-${m.id}`}>
                  <Link2 className="h-3 w-3" /> Link to {m.company}{m.location ? ` · ${m.location}` : ""}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5 flex-shrink-0 h-8" data-testid="universe-banner-add-btn">
          <Plus className="h-3.5 w-3.5" /> Add to Universe
        </Button>
      </div>
      <UniverseCompanyDialog open={addOpen} onOpenChange={setAddOpen} skipDupGate
        initial={{
          company,
          contact_person: record?.end_client_name || (isLead ? record?.name : "") || "",
          contact_number: record?.end_client_phone || (isLead ? record?.phone : "") || "",
          email: record?.end_client_email || (isLead ? record?.email : "") || "",
        }}
        onSaved={(doc) => link({ id: doc.id, company: doc.company })} />
    </div>
  );
}
